import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';

const ResetPassword = ({ onBackToHome }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 10) {
      errors.push('Password must be at least 10 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordErrors = validatePassword(formData.newPassword);
    if (passwordErrors.length > 0) {
      setError(passwordErrors.join('. '));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.resetPassword(token, formData.newPassword);
      
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '20px',
      background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '10px',
      padding: '40px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      maxWidth: '400px',
      width: '100%'
    },
    backButton: {
      background: 'transparent',
      color: '#2c3e50',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      marginBottom: '20px',
      textDecoration: 'underline'
    },
    logo: {
      fontSize: '48px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      marginBottom: '10px',
      color: '#333',
      textAlign: 'center'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '30px',
      textAlign: 'center'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px'
    },
    input: {
      padding: '12px',
      border: '1px solid #ddd',
      borderRadius: '5px',
      fontSize: '16px'
    },
    button: {
      padding: '15px',
      backgroundColor: '#e67e22',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      marginTop: '10px'
    },
    buttonDisabled: {
      backgroundColor: '#6c757d',
      cursor: 'not-allowed'
    },
    error: {
      color: 'red',
      fontSize: '14px',
      marginTop: '10px',
      textAlign: 'center',
      padding: '10px',
      backgroundColor: '#fee',
      borderRadius: '5px'
    },
    success: {
      color: 'green',
      fontSize: '14px',
      marginTop: '10px',
      textAlign: 'center',
      padding: '10px',
      backgroundColor: '#efe',
      borderRadius: '5px'
    },
    loading: {
      color: '#e67e22',
      fontSize: '14px',
      marginTop: '10px',
      textAlign: 'center'
    },
    passwordRequirements: {
      fontSize: '12px',
      color: '#666',
      marginTop: '5px'
    }
  };

  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>✓</div>
          <h1 style={styles.title}>Password Reset Successful!</h1>
          <p style={styles.subtitle}>
            Your password has been reset. Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {onBackToHome && (
          <button onClick={onBackToHome} style={styles.backButton}>
            ← Back to Home
          </button>
        )}
        
        <div style={styles.logo}>🔒</div>
        <h1 style={styles.title}>Reset Your Password</h1>
        <p style={styles.subtitle}>
          Enter your new password below
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            type="password"
            name="newPassword"
            placeholder="New Password"
            value={formData.newPassword}
            onChange={handleInputChange}
            style={styles.input}
            required
            disabled={loading || !token}
          />

          <input
            type="password"
            name="confirmPassword"
            placeholder="Confirm New Password"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            style={styles.input}
            required
            disabled={loading || !token}
          />

          <div style={styles.passwordRequirements}>
            Password must be at least 10 characters with uppercase, lowercase, and special character
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              ...(loading || !token ? styles.buttonDisabled : {})
            }}
            disabled={loading || !token}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        {error && <div style={styles.error}>{error}</div>}
        {loading && <div style={styles.loading}>Processing...</div>}
      </div>
    </div>
  );
};

export default ResetPassword;

